import React, { useCallback, useEffect, useState } from 'react';
import { Box, DialogActions, DialogContent, Fade } from '@mui/material';
import { useForm } from 'react-hook-form';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Dialog,
    DragAndDrop,
    FormLabel,
} from '@biq/apex-ui';
import CodeMirror from '@uiw/react-codemirror';
import { CodeMirrorStyles, commonShowAlert, validationsMessages } from 'lib';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    FileData,
    replaceModalConfig,
} from 'pages/job-manager/components/create-job-dialog';
import {
    commonStyles,
    PipelinesCodeMirrorStyles,
} from 'pages/pipelines/pipelines-helper';
import {
    PipelineTrialRunDefaults,
    TrialRunFormData,
    PipelineTrialRunSchema,
    TrialRunModalProps,
    trialRunModalStyles,
    AccordionPanels,
    ErrorResponseCodeMirrorStyles,
} from './trial-run-modal-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    useFieldMappingTrialRun,
    useJSTransformTrialRun,
    usePipelineTrialRun,
    useRuleSetTrialRun,
} from 'hooks';
import { useAlert, useAuth } from 'core/contexts';
import { CustomIconButton, HelixLoader } from 'components';
import { dracula } from '@uiw/codemirror-theme-dracula';
import { downloadFile } from 'pages/export/export-helper';
import { TrialRunJsonDataEditor } from './TrialRunJsonDataEditor';
import ReadOnlyWrapper from 'components/read-only-wrapper/ReadOnlyWrapper';
import { TrialRunModalHeader } from './TrialRunModalHeader';
import { TrialRunModalFooter } from './TrialRunModalFooter';

export const TrialRunModal = ({
    open,
    handleCloseButton,
    trialRunId,
    trialRunType,
    trialRunPageHeading,
    accordionConfig,
}: TrialRunModalProps): React.JSX.Element => {
    const { getToken } = useAuth();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const {
        control,
        watch,
        setValue,
        clearErrors,
        reset,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<TrialRunFormData>({
        mode: 'all',
        resolver: yupResolver(PipelineTrialRunSchema),
        defaultValues: PipelineTrialRunDefaults,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [openDragAndDropModal, setOpenDragAndDropModal] = useState(false);
    const [files, setFiles] = useState<FileData[]>([]);
    const [prevFiles, setPrevFiles] = useState<FileData[]>([]);
    const [fileError, setFileError] = useState<boolean>(false);
    const [fileTextContent, setFileTextContent] = useState<string>('');
    const [isResultVisible, setIsResultVisible] = useState<boolean>(false);
    const [trialRunSuccessResponse, setTrialRunSuccessResponse] =
        useState<any>(null);
    const [trialRunErrorResponse, setTrialRunErrorResponse] =
        useState<any>(null);
    const [accordionPanelOpen, setAccordionPanelOpen] = useState<
        AccordionPanels | ''
    >('');
    const { mutate: pipelineTrialRun } = usePipelineTrialRun();
    const { mutate: jsTransformTrialRun } = useJSTransformTrialRun();
    const { mutate: fieldMappingTrialRun } = useFieldMappingTrialRun();
    const { mutate: ruleSetTrialRun } = useRuleSetTrialRun();

    useEffect(() => {
        if (!files?.[0]?.url) return;
        setFileError(false);
        fetch(files[0].url)
            .then((res) => res.blob())
            .then((blob) => blob.text())
            .then((textContent) => {
                setFileTextContent(textContent);
            })
            .catch((err) => console.error('Failed to read blob', err));
    }, [files]);

    const resetTrialRunModal = (): void => {
        hideAlert();
        setAccordionPanelOpen('');
        setTrialRunSuccessResponse(null);
        setTrialRunErrorResponse(null);
    };

    useEffect(() => {
        if (!isResultVisible) {
            resetTrialRunModal();
        }
    }, [isResultVisible]);

    const onEditorValueChange = useCallback((value: string) => {
        setValue('data', value);
        if (value) {
            clearErrors('data');
        } else {
            setError('data', {
                type: 'required',
                message: validationsMessages.required,
            });
        }
    }, []);

    const handleResetButtonClick = (): void => {
        onEditorValueChange('');
        setFiles([]);
        setPrevFiles([]);
    };

    const handleUploadFileButtonClick = (): void => {
        setOpenDragAndDropModal(true);
    };

    const onApplyButtonClick = (): void => {
        if (files.length === 0) {
            setFileError(true);
            return;
        }
        setPrevFiles(files);
        setOpenDragAndDropModal(false);
        onEditorValueChange(fileTextContent);
    };

    const onClickDragAndDropModalClose = (): void => {
        setOpenDragAndDropModal(false);
        setFileError(false);
        setFiles(prevFiles);
    };

    const handleTrialRunSuccess = (results: any): void => {
        setTrialRunSuccessResponse(results.data);
        setIsLoading(false);
        setIsResultVisible(true);
    };

    const handleTrialRunError = (error: any): void => {
        setTrialRunErrorResponse(error.data);
        setIsLoading(false);
        setIsResultVisible(true);
    };

    const executeTrialRunByType = (
        callBackFn: Function,
        parsedData: string
    ): void => {
        callBackFn(
            {
                token: getToken() as string,
                id: trialRunId,
                postData: parsedData,
            },
            {
                onSuccess: (results: any): void => {
                    handleTrialRunSuccess(results);
                },
                onError: (error: any): void => {
                    handleTrialRunError(error);
                },
            }
        );
    };

    const onSubmit = (trialRunData: TrialRunFormData): void => {
        let parsedData;
        try {
            parsedData = JSON.parse(trialRunData.data);
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? 'Syntax error(s)'
                    : 'Invalid JSON format';
            const errorDetails = {
                message: errorMessage,
                details: [error instanceof Error && error.message],
            };
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(errorDetails ?? ''),
                showErrorDetails
            );
            setIsResultVisible(false);
            return;
        }
        setIsLoading(true);
        if (trialRunType === 'Pipeline') {
            executeTrialRunByType(pipelineTrialRun, parsedData);
        } else if (trialRunType === 'JavaScript Transform') {
            executeTrialRunByType(jsTransformTrialRun, parsedData);
        } else if (trialRunType === 'Field Mapping') {
            executeTrialRunByType(fieldMappingTrialRun, parsedData);
        } else if (trialRunType === 'Rule Set') {
            executeTrialRunByType(ruleSetTrialRun, parsedData);
        }
    };

    const handleBackButton = (): void => {
        setIsResultVisible(false);
        resetTrialRunModal();
    };

    const handleCloseClick = (): void => {
        handleCloseButton();
        resetTrialRunModal();
    };

    const handleRunClick = (): void => {
        setAccordionPanelOpen('');
        if (isResultVisible) {
            reset({ data: '' });
            clearErrors('data');
            setFiles([]);
            setPrevFiles([]);
            setIsResultVisible(false);
        } else {
            if (!trialRunId) return;
            handleSubmit(onSubmit)();
        }
    };

    const handleDownloadJsonClick = (): void => {
        downloadFile(
            trialRunSuccessResponse,
            `${new Date().toISOString()}.json`,
            'application/json;charset=utf-8;'
        );
    };

    const handleAccordionPanelChange = (
        isExpanded: boolean,
        panel: AccordionPanels
    ): void => {
        setAccordionPanelOpen(isExpanded ? panel : '');
    };

    return (
        <>
            <Dialog
                open={open}
                sx={trialRunModalStyles.root}
                disableRestoreFocus
                closable={false}
                fullWidth
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 0 }}
            >
                {isLoading ? (
                    <HelixLoader />
                ) : (
                    <Box sx={trialRunModalStyles.trialRunContainerStyles}>
                        <TrialRunModalHeader
                            isResultVisible={isResultVisible}
                            handleCloseClick={handleCloseClick}
                            handleBackButton={handleBackButton}
                            trialRunPageHeading={trialRunPageHeading}
                        />
                        <Box
                            sx={{
                                ...trialRunModalStyles.codeMirrorContainer,
                                mb: 2,
                            }}
                        >
                            {accordionConfig && (
                                <>
                                    {isResultVisible ? (
                                        <Accordion
                                            variant='elevation'
                                            expanded={
                                                accordionPanelOpen ===
                                                AccordionPanels.Panel0
                                            }
                                            onChange={(_, isExpanded): void =>
                                                handleAccordionPanelChange(
                                                    isExpanded,
                                                    AccordionPanels.Panel0
                                                )
                                            }
                                            sx={
                                                trialRunModalStyles.accordionContainer
                                            }
                                        >
                                            <AccordionSummary
                                                readOnly={true}
                                                expandIcon={<ExpandMoreIcon />}
                                                header={accordionConfig.heading}
                                            ></AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    ...trialRunModalStyles.accordionDetailsListItemContainer,
                                                    maxHeight:
                                                        trialRunType ===
                                                        'JavaScript Transform'
                                                            ? '349.5px !important'
                                                            : '301.4px !important',
                                                }}
                                            >
                                                {accordionConfig.children}
                                            </AccordionDetails>
                                        </Accordion>
                                    ) : (
                                        <Box
                                            sx={{
                                                ...trialRunModalStyles.fieldGroup,
                                                ...trialRunModalStyles.listItemsContainer,
                                            }}
                                        >
                                            <Box
                                                sx={
                                                    trialRunModalStyles.readOnlyContainerStyles
                                                }
                                            >
                                                <ReadOnlyWrapper
                                                    customTextContainerStyles={
                                                        trialRunModalStyles.wrapperContainer
                                                    }
                                                >
                                                    {''}
                                                </ReadOnlyWrapper>
                                            </Box>
                                            <Box
                                                sx={{
                                                    ...trialRunModalStyles.transformLabelContainer,
                                                    ...trialRunModalStyles.accordionTransformLabelContainer,
                                                }}
                                            >
                                                <FormLabel
                                                    label={
                                                        accordionConfig.heading
                                                    }
                                                    sx={
                                                        trialRunModalStyles.trialRunResponseHeading
                                                    }
                                                />
                                            </Box>
                                            {accordionConfig.children}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                        <Box sx={trialRunModalStyles.codeMirrorContainer}>
                            {isResultVisible ? (
                                <Accordion
                                    variant='elevation'
                                    expanded={
                                        accordionPanelOpen ===
                                        AccordionPanels.Panel1
                                    }
                                    onChange={(_, isExpanded): void =>
                                        handleAccordionPanelChange(
                                            isExpanded,
                                            AccordionPanels.Panel1
                                        )
                                    }
                                >
                                    <AccordionSummary
                                        readOnly={true}
                                        expandIcon={<ExpandMoreIcon />}
                                        header={'JSON Data to Test'}
                                    ></AccordionSummary>
                                    <AccordionDetails
                                        sx={
                                            trialRunModalStyles.accordionDetailsEditorContainer
                                        }
                                    >
                                        <TrialRunJsonDataEditor
                                            control={control}
                                            value={watch('data')}
                                            errors={errors?.data}
                                            readOnlyEditor={isResultVisible}
                                            onEditorValueChange={
                                                onEditorValueChange
                                            }
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            ) : (
                                <Box sx={trialRunModalStyles.fieldGroup}>
                                    <Box
                                        sx={{
                                            ...trialRunModalStyles.transformLabelContainer,
                                            ...trialRunModalStyles.accordionTransformLabelContainer,
                                        }}
                                    >
                                        <FormLabel
                                            required
                                            label='JSON Data to Test'
                                            sx={
                                                trialRunModalStyles.trialRunResponseHeading
                                            }
                                        />
                                        <Box
                                            sx={{
                                                ...trialRunModalStyles.actionsContainer,
                                                ...trialRunModalStyles.accordionActionsContainer,
                                            }}
                                        >
                                            <CustomIconButton
                                                title='Clear JSON'
                                                className='icon-button'
                                                icon='mdi:refresh'
                                                onClick={handleResetButtonClick}
                                            />
                                            <CustomIconButton
                                                title='Upload'
                                                className='icon-button'
                                                icon='mdi:upload'
                                                onClick={
                                                    handleUploadFileButtonClick
                                                }
                                            />
                                        </Box>
                                    </Box>
                                    <TrialRunJsonDataEditor
                                        control={control}
                                        value={watch('data')}
                                        errors={errors.data}
                                        readOnlyEditor={isResultVisible}
                                        onEditorValueChange={
                                            onEditorValueChange
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                        {isResultVisible && (
                            <>
                                {trialRunSuccessResponse && (
                                    <Box
                                        sx={{
                                            ...trialRunModalStyles.codeMirrorContainer,
                                            ...trialRunModalStyles.trialRunResponseContainer,
                                        }}
                                    >
                                        <Box
                                            sx={
                                                trialRunModalStyles.successResultsLabelContainer
                                            }
                                        >
                                            <FormLabel
                                                label='Results'
                                                sx={
                                                    trialRunModalStyles.trialRunResponseHeading
                                                }
                                            />
                                            <CustomIconButton
                                                title='Download'
                                                className='icon-button'
                                                icon='mdi:download'
                                                onClick={
                                                    handleDownloadJsonClick
                                                }
                                            />
                                        </Box>
                                        <CodeMirror
                                            value={JSON.stringify(
                                                trialRunSuccessResponse
                                            )}
                                            readOnly
                                            style={
                                                commonStyles.codeMirrorConatiner
                                            }
                                            height={'120px'}
                                            extensions={[
                                                CodeMirrorStyles,
                                                PipelinesCodeMirrorStyles,
                                            ]}
                                            theme={dracula}
                                        />
                                    </Box>
                                )}
                                {trialRunErrorResponse && (
                                    <Box
                                        sx={{
                                            ...trialRunModalStyles.codeMirrorContainer,
                                            ...trialRunModalStyles.trialRunResponseContainer,
                                        }}
                                    >
                                        <FormLabel
                                            label='Errors'
                                            sx={
                                                trialRunModalStyles.trialRunResponseHeading
                                            }
                                        />
                                        <CodeMirror
                                            value={JSON.stringify(
                                                trialRunErrorResponse
                                            )}
                                            readOnly
                                            style={
                                                commonStyles.codeMirrorConatiner
                                            }
                                            height={'120px'}
                                            extensions={[
                                                CodeMirrorStyles,
                                                PipelinesCodeMirrorStyles,
                                                ErrorResponseCodeMirrorStyles(),
                                            ]}
                                            theme={dracula}
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                        <TrialRunModalFooter
                            isResultVisible={isResultVisible}
                            handleRunClick={handleRunClick}
                        />
                    </Box>
                )}
            </Dialog>
            {openDragAndDropModal && (
                <Dialog
                    sx={trialRunModalStyles.dragAndDropDialogStyles}
                    open={openDragAndDropModal}
                    closable={true}
                    onClose={onClickDragAndDropModalClose}
                    title='Upload File'
                >
                    <DialogContent>
                        <DragAndDrop
                            files={files}
                            setFiles={setFiles}
                            fileError={fileError}
                            setFileError={setFileError}
                            filePreview={true}
                            showReplaceFileModal={true}
                            replaceModalConfig={replaceModalConfig}
                            acceptedFileTypes={{
                                'application/json': ['.json'],
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            autoFocus
                            onClick={onApplyButtonClick}
                            color='primary'
                            variant='contained'
                            size='small'
                        >
                            Apply
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};
